import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Search,
  Users, 
  ShoppingCart, 
  UserCheck, 
  FileText,
  Settings,
  Bell,
  LogOut,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Truck
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../store/store';
import { 
  fetchOrders, 
  searchOrders,
  selectOrders,
  selectOrdersLoading,
  selectOrdersSearching,
  selectOrdersError,
  selectSearchTerm,
  selectSelectedStatusFilter,
  selectSelectedPatientFilter,
  selectSelectedPharmacyFilter,
  setSearchTerm,
  setSelectedStatusFilter,
  setSelectedPatientFilter,
  setSelectedPharmacyFilter
} from '../store/slices/ordersSlice';
import { selectCurrentClinic, selectClinicsLoading } from '../store/slices/authSlice';
import { parseUuidV7 } from '../utils/uuid';
import ResponsiveNavigation from '@/components/ResponsiveNavigation';

const Orders = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentClinic = useSelector(selectCurrentClinic);
  const clinicsLoading = useSelector(selectClinicsLoading);
  const orders = useSelector(selectOrders);
  const loading = useSelector(selectOrdersLoading);
  const searching = useSelector(selectOrdersSearching);
  const error = useSelector(selectOrdersError);
  const searchTerm = useSelector(selectSearchTerm);
  const selectedStatusFilter = useSelector(selectSelectedStatusFilter);
  const selectedPatientFilter = useSelector(selectSelectedPatientFilter);
  const selectedPharmacyFilter = useSelector(selectSelectedPharmacyFilter);
  
  const [selectedDateRange, setSelectedDateRange] = useState('all');

  // Load orders when component mounts or clinic changes
  useEffect(() => {
    // GATE: Wait for clinic loading to complete
    if (clinicsLoading) {
      console.log('[Orders] Waiting for clinics to load...');
      return;
    }

    // GATE: Ensure we have clinic data with orders href
    if (!currentClinic?.orders?.href) {
      console.log('[Orders] No orders href available yet');
      return;
    }

    // NOW SAFE: Make API call
    console.log('[Orders] Loading orders from:', currentClinic.orders.href);
    dispatch(fetchOrders({ ordersHref: currentClinic.orders.href }));
  }, [dispatch, currentClinic, clinicsLoading]);

  // Handle search term changes
  const handleSearchChange = (value: string) => {
    dispatch(setSearchTerm(value));
    if (value.trim() && currentClinic?.orders?.href) {
      dispatch(searchOrders({ ordersHref: currentClinic.orders.href, searchTerm: value.trim() }));
    } else if (currentClinic?.orders?.href) {
      dispatch(fetchOrders({ ordersHref: currentClinic.orders.href }));
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Processing':
        return <Clock className="w-4 h-4" />;
      case 'Shipped':
        return <Truck className="w-4 h-4" />;
      case 'Delivered':
        return <CheckCircle className="w-4 h-4" />;
      case 'Cancelled':
        return <XCircle className="w-4 h-4" />;
      case 'Pending':
        return <Package className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-orange-100 text-orange-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get medication names display from formulary
  const getMedicationNames = (formulary: any): string => {
    if (!formulary?.medications || formulary.medications.length === 0) return 'Unknown Medication';
    return formulary.medications.map((med: any) => med.name).join('  •  ');
  };

  // Helper function to get full quantity display with bullet points and total
  const getFullQuantityDisplay = (formulary: any): string => {
    if (!formulary?.medications || formulary.medications.length === 0) {
      return formulary?.formulary_size ? `Total: ${formulary.formulary_size.amount} ${formulary.formulary_size.unit}` : 'N/A';
    }
    
    // Get medication quantities without rate
    const medicationQuantities = formulary.medications.map((med: any) => 
      `${med.amount} ${med.unit}`
    ).join(' • ');
    
    // Add formulary rate and total quantity
    if (formulary.formulary_rate && formulary.formulary_size) {
      return `${medicationQuantities} / ${formulary.formulary_rate.amount} ${formulary.formulary_rate.unit}        |     ${formulary.formulary_size.amount} ${formulary.formulary_size.unit}`;
    }
    
    return medicationQuantities;
  };

  // Helper function to get pharmacy name from formulary
  const getPharmacyName = (formulary: any): string => {
    return formulary?.pharmacy?.name || 'Unknown Pharmacy';
  };

  // Helper function to get shipping method from formulary
  const getShippingMethod = (formulary: any): string => {
    return formulary?.pharmacy?.shipping || 'standard';
  };

  // Helper function to get ship-to destination
  const getShipToDestination = (order: any): string => {
    return order.ship_to_patient ? 'to patient' : 'to clinic';
  };

  // Filter orders based on current filters (client-side filtering for now)
  const filteredOrders = orders.filter(order => {
    const matchesStatus = selectedStatusFilter.length === 0 || selectedStatusFilter.includes(order.status);
    const matchesPatient = !selectedPatientFilter || order.patient_name?.includes(selectedPatientFilter);
    const matchesPharmacy = !selectedPharmacyFilter || getPharmacyName(order.formulary)?.includes(selectedPharmacyFilter);

    return matchesStatus && matchesPatient && matchesPharmacy;
  });

  return (
    <div className="min-h-screen bg-gray-50 font-lato">
      <ResponsiveNavigation />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:pl-24 lg:pr-8 py-8">
        {/* Tabs */}
        <Tabs defaultValue="all-orders" className="mb-6">
          <TabsList className="grid w-full grid-cols-3 bg-white border">
            <TabsTrigger value="all-orders" className="data-[state=active]:bg-vitl-green data-[state=active]:text-white font-lato">
              All Orders
            </TabsTrigger>
            <TabsTrigger value="in-progress" className="data-[state=active]:bg-vitl-green data-[state=active]:text-white font-lato">
              In Progress
            </TabsTrigger>
            <TabsTrigger value="completed" className="data-[state=active]:bg-vitl-green data-[state=active]:text-white font-lato">
              Completed
            </TabsTrigger>
          </TabsList>

          {/* Search Bar - Always visible */}
          <div className="relative mb-6 mt-6">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-vitl-dark/50" />
            <Input
              type="text"
              placeholder="Search orders, patients, or order IDs"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10 h-12 bg-gray-100 border-0 focus:ring-2 focus:ring-vitl-green/20 focus:border-vitl-green font-lato"
            />
          </div>

          <TabsContent value="all-orders">
          

            {/* Loading State - Show during clinic loading OR orders loading */}
            {(loading || searching || clinicsLoading) && (
              <div className="text-center py-12">
                <p className="text-vitl-dark/70 font-lato">
                  {searching ? 'Searching...' : 
                   clinicsLoading ? 'Loading clinic data...' : 
                   'Loading orders...'}
                </p>
              </div>
            )}

            {/* Error State */}
            {error && !clinicsLoading && (
              <div className="text-center py-12">
                <p className="text-red-600 font-lato">Error: {error}</p>
              </div>
            )}

            {/* Orders List */}
            {!loading && !searching && !error && !clinicsLoading && (
              <div className="space-y-4">
                {filteredOrders.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-vitl-dark/70 font-lato">No orders found matching your filters.</p>
                  </div>
                ) : (
                  filteredOrders.map((order) => (
                    <Card key={order.id} className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200 shadow-emerald-100/50">
                      <CardContent className="p-6">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                          <div className="lg:col-span-2 min-w-0">
                            <p className="font-lato font-medium text-vitl-navy text-sm whitespace-nowrap overflow-hidden text-ellipsis">{parseUuidV7(order.id)}</p>
                          </div>
                          
                          <div className="lg:col-span-2">
                            <h3 className="font-lato font-medium text-vitl-navy text-lg mb-1">{getMedicationNames(order.formulary)}</h3>
                            <p className="text-vitl-dark/70 font-lato text-sm font-medium">{getFullQuantityDisplay(order.formulary)}</p>
                            <p className="text-vitl-dark/50 font-lato text-xs">Qty: {order.quantity}</p>
                            {order.directions && (
                              <p className="text-vitl-dark/70 font-lato text-sm">{order.directions}</p>
                            )}
                          </div>

                          <div className="lg:col-span-2">
                            <div>
                              <p className="text-vitl-dark/50 text-xs font-lato">Patient</p>
                              <p className="text-vitl-navy font-lato text-sm">{order.patient_name || 'Unknown Patient'}</p>
                            </div>
                          </div>

                          <div className="lg:col-span-2">
                            <div>
                              <p className="text-vitl-dark/50 text-xs font-lato">Pharmacy</p>
                              <p className="text-vitl-navy font-lato text-sm">{getPharmacyName(order.formulary)}</p>
                              <p className="text-vitl-dark/90 font-lato text-xs capitalize">{getShippingMethod(order.formulary)} shipping {getShipToDestination(order)}</p>
                            </div>
                          </div>

                          <div className="lg:col-span-2">
                            <div>
                              <p className="text-vitl-dark/50 text-xs font-lato">Order Date</p>
                              <p className="text-vitl-navy font-lato text-sm">{new Date(order.order_date).toLocaleDateString()}</p>
                            </div>
                          </div>

                          <div className="lg:col-span-1">
                            <span className={`${getStatusColor(order.status)} px-2 py-1 text-xs font-lato rounded border flex items-center space-x-1`}>
                              {getStatusIcon(order.status)}
                              <span className="capitalize">{order.status}</span>
                            </span>
                          </div>

                          <div className="lg:col-span-1 text-right">
                            <p className="font-lato font-medium text-vitl-navy text-lg">{order.total_price ? `$${order.total_price.toFixed(2)}` : 'N/A'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </TabsContent>


          <TabsContent value="in-progress">
            {/* Loading State for In-Progress */}
            {(loading || searching || clinicsLoading) && (
              <div className="text-center py-12">
                <p className="text-vitl-dark/70 font-lato">
                  {searching ? 'Searching...' : 
                   clinicsLoading ? 'Loading clinic data...' : 
                   'Loading orders...'}
                </p>
              </div>
            )}
            
            {!loading && !searching && !error && !clinicsLoading && (
              <div className="space-y-4">
                {filteredOrders.filter(order => ['processing', 'shipped'].includes(order.status.toLowerCase())).map((order) => (
                  <Card key={order.id} className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200 shadow-emerald-100/50">
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                        <div className="lg:col-span-2 min-w-0">
                          <p className="font-lato font-medium text-vitl-navy text-sm whitespace-nowrap overflow-hidden text-ellipsis">{parseUuidV7(order.id)}</p>
                        </div>
                        
                        <div className="lg:col-span-2">
                          <h3 className="font-lato font-medium text-vitl-navy text-lg mb-1">{getMedicationNames(order.formulary)}</h3>
                          <p className="text-vitl-dark/70 font-lato text-sm font-medium">{getFullQuantityDisplay(order.formulary)}</p>
                          <p className="text-vitl-dark/50 font-lato text-xs">Qty: {order.quantity}</p>
                          {order.directions && (
                            <p className="text-vitl-dark/70 font-lato text-sm">{order.directions}</p>
                          )}
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Patient</p>
                            <p className="text-vitl-navy font-lato text-sm">{order.patient_name || 'Unknown Patient'}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Pharmacy</p>
                            <p className="text-vitl-navy font-lato text-sm">{getPharmacyName(order.formulary)}</p>
                            <p className="text-vitl-dark/90 font-lato text-xs capitalize">{getShippingMethod(order.formulary)} shipping {getShipToDestination(order)}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Order Date</p>
                            <p className="text-vitl-navy font-lato text-sm">{new Date(order.order_date).toLocaleDateString()}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-1">
                          <span className={`${getStatusColor(order.status)} px-2 py-1 text-xs font-lato rounded border flex items-center space-x-1`}>
                            {getStatusIcon(order.status)}
                            <span className="capitalize">{order.status}</span>
                          </span>
                        </div>

                        <div className="lg:col-span-1 text-right">
                          <p className="font-lato font-medium text-vitl-navy text-lg">{order.total_price ? `$${order.total_price.toFixed(2)}` : 'N/A'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed">
            {/* Loading State for Completed */}
            {(loading || searching || clinicsLoading) && (
              <div className="text-center py-12">
                <p className="text-vitl-dark/70 font-lato">
                  {searching ? 'Searching...' : 
                   clinicsLoading ? 'Loading clinic data...' : 
                   'Loading orders...'}
                </p>
              </div>
            )}
            
            {!loading && !searching && !error && !clinicsLoading && (
              <div className="space-y-4">
                {filteredOrders.filter(order => ['delivered', 'cancelled'].includes(order.status.toLowerCase())).map((order) => (
                  <Card key={order.id} className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200 shadow-emerald-100/50">
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                        <div className="lg:col-span-2 min-w-0">
                          <p className="font-lato font-medium text-vitl-navy text-sm whitespace-nowrap overflow-hidden text-ellipsis">{parseUuidV7(order.id)}</p>
                        </div>
                        
                        <div className="lg:col-span-2">
                          <h3 className="font-lato font-medium text-vitl-navy text-lg mb-1">{getMedicationNames(order.formulary)}</h3>
                          <p className="text-vitl-dark/70 font-lato text-sm font-medium">{getFullQuantityDisplay(order.formulary)}</p>
                          <p className="text-vitl-dark/50 font-lato text-xs">Qty: {order.quantity}</p>
                          {order.directions && (
                            <p className="text-vitl-dark/70 font-lato text-sm">{order.directions}</p>
                          )}
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Patient</p>
                            <p className="text-vitl-navy font-lato text-sm">{order.patient_name || 'Unknown Patient'}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Pharmacy</p>
                            <p className="text-vitl-navy font-lato text-sm">{getPharmacyName(order.formulary)}</p>
                            <p className="text-vitl-dark/90 font-lato text-xs capitalize">{getShippingMethod(order.formulary)} shipping {getShipToDestination(order)}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Order Date</p>
                            <p className="text-vitl-navy font-lato text-sm">{new Date(order.order_date).toLocaleDateString()}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-1">
                          <span className={`${getStatusColor(order.status)} px-2 py-1 text-xs font-lato rounded border flex items-center space-x-1`}>
                            {getStatusIcon(order.status)}
                            <span className="capitalize">{order.status}</span>
                          </span>
                        </div>

                        <div className="lg:col-span-1 text-right">
                          <p className="font-lato font-medium text-vitl-navy text-lg">{order.total_price ? `$${order.total_price.toFixed(2)}` : 'N/A'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Orders;
